import { getRoomName } from '../base/conference/functions';
import { getDialOutStatusUrl, getDialOutUrl } from '../base/config/functions';
import { MEETING_NAME_ENABLED, UNSAFE_ROOM_WARNING, getFeatureFlag } from '../base/flags';
import { isAudioMuted, isVideoMutedByUser } from '../base/media/functions';
import { isDevModeEnabled } from '../base/util/devUtils';

const PARTICIPANT_COUNT_BYPASS_DOMAINS: $ReadOnlyArray<string> = Object.freeze([
    'meethour.us'
]);

/**
 * Normalizes a host/domain by lowercasing and stripping protocol and path.
 *
 * @param {string} value - Host or domain value.
 * @returns {string}
 */
function normalizeHostOrDomain(value?: string): string {
    return typeof value === 'string'
        ? value.trim().toLowerCase()
            .replace(/^https?:\/\//, '')
            .replace(/\/.*$/, '')
        : '';
}

/**
 * Checks whether a host/domain is included in the bypass list.
 *
 * @param {string} hostOrDomain - Normalized host or domain.
 * @returns {boolean}
 */
function isHostInBypassDomains(hostOrDomain: string): boolean {
    return PARTICIPANT_COUNT_BYPASS_DOMAINS.some(domain =>
        hostOrDomain === domain || hostOrDomain.endsWith(`.${domain}`));
}


/**
 * Selector for the visibility of the 'join by phone' button.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isJoinByPhoneButtonVisible(state: Object): boolean {
    return Boolean(getDialOutUrl(state) && getDialOutStatusUrl(state));
}

/**
 * Selector for the visibility of the 'join by phone' button.
 *
 * @param {string} siteKey - The state of the app.
 * @returns {boolean}
 */
export const loadReCaptcha = (siteKey: string) => {
    if (document && document.body) {
        const script = document.createElement('script');

        script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${siteKey}`;
        document.body && document.body.appendChild(script);
    }
};

/**
 * Returns true when participant-count checks should be bypassed on prejoin.
 *
 * @param {string} domain - Optional configured backend domain.
 * @returns {boolean}
 */
export function shouldBypassParticipantCountGate(domain?: string): boolean {
    const hostname = normalizeHostOrDomain(
        (typeof window !== 'undefined' && window.location && window.location.hostname) || ''
    );
    const normalizedDomain = normalizeHostOrDomain(domain);

    return isDevModeEnabled()
        || isHostInBypassDomains(hostname)
        || isHostInBypassDomains(normalizedDomain);
}

/**
 * Returns true when participant count is a valid non-negative number.
 *
 * @param {number|string} participantCount - Participant count from API/state.
 * @returns {boolean}
 */
export function hasValidParticipantCount(participantCount: ?(number | string)): boolean {
    const parsedCount = Number(participantCount);

    return Number.isFinite(parsedCount) && parsedCount >= 0;
}

/**
 * Selector for determining if the device status strip is visible or not.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isDeviceStatusVisible(state: Object): boolean {
    return !(isAudioMuted(state) && isVideoMutedByUser(state))
    && !state['features/base/config'].startSilent;
}

/**
 * Selector for determining if the display name is mandatory.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isDisplayNameRequired(state: Object): boolean {
    return state['features/prejoin'].isDisplayNameRequired
        || state['features/base/config'].requireDisplayName;
}

/**
 * Selector for determining if the Email is mandatory.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function disableEmailOnPrejoin(state: Object): boolean {
    return state['features/base/config'].disableEmail;
}

/**
 * Selector for determining if the user has chosen to skip prejoin page.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isPrejoinSkipped(state: Object) {
    return state['features/prejoin'].userSelectedSkipPrejoin;
}

/**
 * Returns the text for the prejoin status bar.
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getDeviceStatusText(state: Object): string {
    return state['features/prejoin']?.deviceStatusText;
}

/**
 * Returns the type of the prejoin status bar: 'ok'|'warning'.
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getDeviceStatusType(state: Object): string {
    return state['features/prejoin']?.deviceStatusType;
}

/**
 * Returns the 'conferenceUrl' used for dialing out.
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getDialOutConferenceUrl(state: Object): string {
    return `${getRoomName(state)}@${state['features/base/config'].hosts.muc}`;
}

/**
 * Selector for getting the dial out country.
 *
 * @param {Object} state - The state of the app.
 * @returns {Object}
 */
export function getDialOutCountry(state: Object): Object {
    return state['features/prejoin'].dialOutCountry;
}

/**
 * Selector for getting the dial out number (without prefix).
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getDialOutNumber(state: Object): string {
    return state['features/prejoin'].dialOutNumber;
}

/**
 * Selector for getting the dial out status while calling.
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getDialOutStatus(state: Object): string {
    return state['features/prejoin'].dialOutStatus;
}

/**
 * Returns the full dial out number (containing country code and +).
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getFullDialOutNumber(state: Object): string {
    const dialOutNumber = getDialOutNumber(state);
    const country = getDialOutCountry(state);

    return `+${country.dialCode}${dialOutNumber}`;
}

/**
 * Selector for getting the error if any while creating streams.
 *
 * @param {Object} state - The state of the app.
 * @returns {string}
 */
export function getRawError(state: Object): string {
    return state['features/prejoin']?.rawError;
}

/**
 * Selector for getting the visiblity state for the 'JoinByPhoneDialog'.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isJoinByPhoneDialogVisible(state: Object): boolean {
    return state['features/prejoin']?.showJoinByPhoneDialog;
}

/**
 * Returns true if the prejoin page is enabled and no flag
 * to bypass showing the page is present.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isPrejoinPageEnabled(state: Object): boolean {
    return state['features/base/config'].prejoinPageEnabled
        && !state['features/base/settings'].userSelectedSkipPrejoin
        && !(state['features/base/config'].enableForcedReload && state['features/prejoin'].skipPrejoinOnReload);
}

/**
 * Returns true if the prejoin page is visible & active.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isPrejoinPageVisible(state: Object): boolean {
    return isPrejoinPageEnabled(state) && state['features/prejoin']?.showPrejoin;
}

/**
 * Returns true if we should auto-knock in case lobby is enabled for the room.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function shouldAutoKnock(state: Object): boolean {
    const { iAmRecorder, iAmSipGateway } = state['features/base/config'];

    return (isPrejoinPageEnabled(state) || (iAmRecorder && iAmSipGateway))
        && !state['features/lobby'].knocking;
}

/**
 * Returns true if we should auto-knock in case lobby is enabled for the room.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function shouldKnockBasedOnCriteria(state): boolean {
    const { iAmRecorder, prejoinPageEnabled } = state['features/base/config'];

    // Removing this code from below line from return statement to fix lobby bug when prejoin page is disabled and desktop deep link is enabled. March 25 2024.
    // && !interfaceConfig?.ENABLE_DESKTOP_DEEPLINK;

    return !prejoinPageEnabled && !iAmRecorder;
}

/**
 * Returns true if the unsafe room warning flag is enabled.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isUnsafeRoomWarningEnabled(state): boolean {
    const { enableInsecureRoomNameWarning = false } = state['features/base/config'];

    return getFeatureFlag(state, UNSAFE_ROOM_WARNING, enableInsecureRoomNameWarning);
}


/**
 * Returns true if the room name is enabled.
 *
 * @param {IReduxState} state - The state of the app.
 * @returns {boolean}
 */
export function isRoomNameEnabled(state): boolean {
    return getFeatureFlag(state, MEETING_NAME_ENABLED, true)
        || !state['features/base/config'].hideConferenceSubject;
}

